import { Box, Card, CardActionArea, CardContent, CardHeader, CardMedia, Chip, Stack, Theme, Typography, useTheme } from "@mui/material"
import { ResourceWrapper } from "../../../../../eventer-pod/client/ResourceWrapper.ts";
import React, { useContext, useEffect, useState } from "react";
import Session from "../../../../../appfw/authentication/Session.ts";
import { SessionContext } from "../../../../../appfw/authentication/SessionProvider.tsx";
import Semantic from "../../../../../eventer/Semantic.ts";
import { LocalEventerPod } from "../../../../../eventer-pod/client/local-eventer-pod.ts";
import {formatDateTime} from "@eventer/api-client";

interface EventStatusInfo {
    label: string
    color: string
    textColor: string
    opacity: number
}

const eventStatusColors = {
    published: { bg: "#4a8c5c", text: "#ffffff" },
    unpublished: { bg: "#b07d4f", text: "#ffffff" },
} as const

function getEventStatusInfo(eventStatus: string | undefined, _theme: Theme): EventStatusInfo {
    if (eventStatus === Semantic.SchemaOrg.Predicates.eventStatus.eventScheduled) {
        return { label: "Öffentlich", color: eventStatusColors.published.bg, textColor: eventStatusColors.published.text, opacity: 1 }
    }
    return { label: "Nicht öffentlich", color: eventStatusColors.unpublished.bg, textColor: eventStatusColors.unpublished.text, opacity: 0.7 }
}

interface EventCardProps {
    pod: LocalEventerPod
    event: ResourceWrapper
}

export function EventCard(props: EventCardProps) {

    const session = useContext<undefined | null | Session>(SessionContext)
    const [image, setImage] = useState<undefined | null | ResourceWrapper>(undefined)

    const theme = useTheme()

    useEffect(() => {

        const sessionId = session?.id
        if (sessionId === undefined) {
            return;
        }

        const imageUri = props.event.getFirstString("https://schema.org/image")
        if (!imageUri) {
            return
        }

        props.pod.getResource({ uri: imageUri, sessionId: sessionId, importOptions: { ifMissing: true } })
            .then((resource) => {
                setImage(resource)
            })
    }, [props.event, props.pod, session])

    const title = props.event.getFirstString("https://schema.org/name") ?? "(ohne Titel)"
    const description = props.event.getFirstString("https://schema.org/description")
    const keywords = props.event.getAllStrings("https://schema.org/keywords")
    const startDate = props.event.getFirstString("https://schema.org/startDate")
    const parsedDate = startDate ? new Date(startDate) : null
    let dateText = parsedDate ? formatDateTime({ date: parsedDate }) : undefined

    const eventStatus = props.event.getFirstString(Semantic.SchemaOrg.Predicates.eventStatus.uri)
    const statusInfo = getEventStatusInfo(eventStatus, theme)

    let mediaEl: undefined | React.JSX.Element = undefined
    if (image) {
        const imageContentUrl = image.getFirstString("https://schema.org/contentUrl")
        if (imageContentUrl) {
            mediaEl = (
                <CardMedia
                    component="img"
                    height="300"
                    image={imageContentUrl}
                    alt={image.getFirstString("https://schema.org/caption")}
                    sx={{ maxHeight: 300, objectFit: 'cover' }}
                />
            )
        }
    }

    return (
        <Box sx={{ p: 1 }} data-anchor-id={props.event.subject}>
            <Card sx={{
                opacity: statusInfo.opacity,
                borderLeft: `4px solid ${statusInfo.color}`,
            }}>
                <CardHeader
                    title={title}
                    subheader={dateText}
                />
                <CardActionArea>
                    {mediaEl}
                    <CardContent>
                        {description && description.length > 0 &&
                            (
                                <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                                    {description}
                                </Typography>
                            )
                        }
                        <Stack direction="row" spacing={1} sx={{ mt: 2, mb: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                            <Chip label={statusInfo.label} size="small" sx={{ backgroundColor: statusInfo.color, color: statusInfo.textColor }} />
                            {keywords?.map((keyword) => (
                                <Chip key={keyword} label={keyword} size="small" />
                            ))}
                        </Stack>
                    </CardContent>
                </CardActionArea>
            </Card>
        </Box>
    )
}
